import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import SEO from '@/components/SEO';
import { TrendingUp, ArrowRight, Building2, Leaf, CheckCircle, DollarSign, Users, BarChart3, Zap, MapPin } from 'lucide-react';
import { site } from '@/data/content';

export default function CaseStudies() {
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: 'ZuriHub Customer Case Studies',
    description: 'Learn how businesses like CodeLab and Pumzi Teas achieved success with ZuriHub customer experience platform.',
    url: `${site.baseUrl}/case-studies`,
  };

  useEffect(() => {
    document.title = 'Customer Case Studies - ZuriHub';
  }, []);

  const caseStudies = [
    {
      id: 'codelab',
      title: 'How CodeLab Increased Sales by 47%',
      description: 'Software development company reduced costs by 40% and boosted sales using ZuriHub Enterprise and Content Lab',
      industry: 'Software Development',
      location: 'Uganda',
      metrics: [
        { label: 'Sales Increase', value: '47%' },
        { label: 'Monthly Savings', value: '$260' },
        { label: 'Team Size', value: '10 agents' },
      ],
      features: ['Content Lab', 'Unified Inbox', 'Zyra AI', 'Social Media Management'],
      icon: 'Building2',
      color: 'from-blue-500 to-indigo-500',
      link: '/case-study/codelab',
    },
    {
      id: 'pumzi-teas',
      title: 'How Pumzi Teas Achieved 40% Customer Retention',
      description: 'Organic tea e-commerce company transformed customer experience with live chat, knowledge base, and behavioral analytics',
      industry: 'E-Commerce / Organic Tea',
      location: 'Kenya',
      metrics: [
        { label: 'Customer Retention', value: '40%' },
        { label: 'CSAT Score', value: '85%' },
        { label: 'Conversion Increase', value: '35%' },
      ],
      features: ['Live Chat', 'Knowledge Base', 'WhatsApp Channel', 'Live Visitor', 'Heatmaps', 'Screen Recording'],
      icon: 'Leaf',
      color: 'from-green-500 to-emerald-500',
      link: '/case-study/pumzi-teas',
    },
  ];

  return (
    <>
      <SEO
        title="Customer Case Studies - ZuriHub"
        description="Learn how businesses like CodeLab and Pumzi Teas achieved success with ZuriHub customer experience platform. Real results, real stories."
        path="/case-studies"
        jsonLd={[jsonLd]}
      />

      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
        {/* Hero Section */}
        <div className="bg-gradient-to-br from-slate-900 via-blue-900 to-indigo-900 text-white py-20 md:py-28 relative overflow-hidden">
          {/* Background Pattern */}
          <div className="absolute inset-0 opacity-10">
            <div className="absolute inset-0" style={{
              backgroundImage: 'radial-gradient(circle at 20% 50%, rgba(255,255,255,0.1) 0%, transparent 50%), radial-gradient(circle at 80% 50%, rgba(255,255,255,0.1) 0%, transparent 50%)'
            }}></div>
          </div>
          
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
            <div className="max-w-4xl text-center">
              <div className="inline-flex items-center gap-2 bg-gradient-to-r from-amber-500 to-orange-500 text-white px-4 py-2 rounded-full mb-6 shadow-lg">
                <TrendingUp className="h-4 w-4" />
                <span className="text-sm font-semibold tracking-wide uppercase">Customer Success Stories</span>
              </div>
              
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight tracking-tight">
                Real Results from <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-indigo-400">Real Businesses</span>
              </h1>
              
              <p className="text-xl md:text-2xl text-blue-100 mb-8 leading-relaxed max-w-3xl mx-auto">
                Discover how companies across industries are transforming their customer experience and driving growth with ZuriHub
              </p>
              
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <Link
                  to="/book-a-demo"
                  className="inline-flex items-center gap-2 rounded-full bg-white px-8 py-4 text-base font-bold text-brand-600 transition hover:bg-slate-100"
                >
                  Start Your Success Story
                  <ArrowRight className="h-5 w-5" />
                </Link>
                <Link
                  to="/pricing"
                  className="inline-flex items-center gap-2 rounded-full border-2 border-white px-8 py-4 text-base font-bold text-white transition hover:bg-white/10"
                >
                  View Pricing
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Case Studies Grid */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {caseStudies.map((study) => {
              const StudyIcon = study.icon === 'Building2' ? Building2 : Leaf;
              return (
                <Link
                  key={study.id}
                  to={study.link}
                  className="group bg-white rounded-3xl border border-slate-200 overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2"
                >
                  <div className={`bg-gradient-to-r ${study.color} p-8 text-white relative overflow-hidden`}>
                    {/* Decorative pattern */}
                    <div className="absolute inset-0 opacity-10">
                      <div className="absolute inset-0" style={{
                        backgroundImage: 'radial-gradient(circle at 20% 50%, rgba(255,255,255,0.2) 0%, transparent 50%), radial-gradient(circle at 80% 50%, rgba(255,255,255,0.2) 0%, transparent 50%)'
                      }}></div>
                    </div>
                    
                    <div className="relative">
                      <div className="flex items-center gap-4 mb-4">
                        <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-white/20 backdrop-blur-sm shadow-lg">
                          <StudyIcon className="h-8 w-8" />
                        </div>
                        <div>
                          <div className="text-sm font-semibold opacity-90 uppercase tracking-wide">{study.industry}</div>
                          <div className="text-sm opacity-75 flex items-center gap-1">
                            <MapPin className="h-3 w-3" />
                            {study.location}
                          </div>
                        </div>
                      </div>
                      <h2 className="text-2xl font-bold mb-3 group-hover:text-white transition leading-tight">
                        {study.title}
                      </h2>
                      <p className="text-white/90 leading-relaxed">
                        {study.description}
                      </p>
                    </div>
                  </div>
                  
                  <div className="p-8">
                    {/* Metrics */}
                    <div className="grid grid-cols-3 gap-4 mb-6">
                      {study.metrics.map((metric, i) => (
                        <div key={i} className="text-center bg-gradient-to-br from-slate-50 to-slate-100 rounded-xl p-4">
                          <div className="text-2xl font-extrabold text-slate-900">{metric.value}</div>
                          <div className="text-xs text-slate-600 mt-1 font-medium">{metric.label}</div>
                        </div>
                      ))}
                    </div>

                    {/* Features */}
                    <div className="mb-6">
                      <div className="text-sm font-bold text-slate-900 mb-3 flex items-center gap-2">
                        <Zap className="h-4 w-4 text-brand-600" />
                        Key Features Used
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {study.features.map((feature, i) => (
                          <span
                            key={i}
                            className="inline-flex items-center gap-1 bg-brand-50 text-brand-700 px-3 py-1.5 rounded-full text-xs font-semibold border border-brand-100"
                          >
                            <CheckCircle className="h-3 w-3 text-brand-600" />
                            {feature}
                          </span>
                        ))}
                      </div>
                    </div>

                    {/* CTA */}
                    <div className="flex items-center gap-2 text-brand-600 font-bold group-hover:gap-3 transition-all">
                      Read Full Case Study
                      <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>

        {/* Key Metrics Section */}
        <div className="bg-slate-50 py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-slate-900 mb-4">Proven Results Across Industries</h2>
              <p className="text-lg text-slate-600 max-w-2xl mx-auto">
                Our customers see measurable improvements in customer satisfaction, retention, and business growth
              </p>
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              {[
                { icon: 'TrendingUp', value: '47%', label: 'Average Sales Increase' },
                { icon: 'Users', value: '40%', label: 'Customer Retention Rate' },
                { icon: 'DollarSign', value: '$260', label: 'Average Monthly Savings' },
                { icon: 'BarChart3', value: '85%', label: 'Customer Satisfaction' },
              ].map((stat, i) => {
                const StatIcon = stat.icon === 'TrendingUp' ? TrendingUp : stat.icon === 'Users' ? Users : stat.icon === 'DollarSign' ? DollarSign : BarChart3;
                return (
                  <div key={i} className="text-center">
                    <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-r from-brand-500 to-indigo-500 text-white mx-auto mb-4">
                      <StatIcon className="h-8 w-8" />
                    </div>
                    <div className="text-4xl font-extrabold text-slate-900 mb-2">{stat.value}</div>
                    <div className="text-sm text-slate-600">{stat.label}</div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="bg-gradient-to-r from-brand-600 to-indigo-600 py-16">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl font-bold text-white mb-4">Ready to Write Your Success Story?</h2>
            <p className="text-xl text-blue-100 mb-8">
              Join hundreds of businesses transforming their customer experience with ZuriHub
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                to="/book-a-demo"
                className="inline-flex items-center gap-2 rounded-full bg-white px-8 py-4 text-base font-bold text-brand-600 transition hover:bg-slate-100"
              >
                Book a Demo
                <ArrowRight className="h-5 w-5" />
              </Link>
              <Link
                to="/pricing"
                className="inline-flex items-center gap-2 rounded-full border-2 border-white px-8 py-4 text-base font-bold text-white transition hover:bg-white/10"
              >
                View Pricing
              </Link>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}